import { LitElement, html, css, nothing, TemplateResult, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { when } from 'lit/directives/when.js';
import { ContextProvider } from '@lit/context';
import {
  ltnRouterContext,
  ltnRouteContext,
  ltnToastServiceContext,
  ltnDialogServiceContext,
  nsLoaderContext,
  nsCommuncationServiceContext,
  publicLedgerServiceContext,
  nsStripeServiceContext,
  nsWorkflowServiceContext,
  nsSettingsServiceContext,
  nsSettingsContext,
  ltnAuthServiceContext,
  ltnAuthStateContext,
  nsDirectoryServiceContext,
  nsDirectoryStateContext,
  nsTrustScoreServiceContext,
  nsTutorialServiceContext,
} from '../ns-services/contexts.js';

import { timeOut } from '../ltn-helpers/utils/async.js';
import { Debouncer } from '../ltn-helpers/utils/debouncer.js';
import { LtnRouter, Route } from '../ltn-router/ltn-router.js';
import { LtnAuthService, AuthState as LtnAuthState } from '../ltn-auth/ltn-auth-service.js';
import { LtnToastService } from '../ltn-toast/ltn-toast-service.js';
import { LtnDialogService } from '../ltn-dialog/ltn-dialog-service.js';

import { FinalFormController, FormConfig } from '../ns-form/final-form-controller.js';

// import { LtnGoogleService } from '@lighten/components/ltn-google/ltn-google-service.js';

import { ButtressDbService, buttressDbServiceContext, Settings } from '@buttress/crag';
import { DbReadActivity } from '../helpers/db-read-activity.js';
import { reloadBehindShutter } from '../helpers/reload-behind-shutter.js';

import { NSWorkflowService } from '../ns-workflows/ns-workflow-service.js';
import { NSTutorialService } from '../ns-tutorial/ns-tutorial-service.js';

import {
  nsSharedStyles,
  nsAnimationStyles,
  nsColours,
  nsLightTheme,
  nsCardStyles,
  nsLayoutStyles,
} from '../ns-styles/index.js';
import { viewPath } from '../helpers/route.js';
import {
  isFreshArrival,
  markNotificationsRead,
  notificationPath,
  notificationToast,
} from '../helpers/notifications.js';
import type Notification from '../models/notification.js';
import { reduceWebAnimations } from '../helpers/motion.js';
import { applyReduceMotionSetting } from '../ns-styles/reduce-motion-setting.js';
import { renderFormErrorSpan } from '../helpers/dom.js';
import { base64UrlDecode } from '../helpers/common.js';
import {
  emailLinkToken,
  isValidEmailCode,
  renderEmailCodeCard,
  renderEmailLinkCard,
  renderProviderButton,
} from '../helpers/auth-ui.js';

// Local Services
import { ErrorDirectory } from '../ns-services/ns-error-directory.js';
import { NSSettingsService, SettingsObject } from '../ns-settings/ns-settings-service.js';
import { dataScreenOpen, parseDataPath } from '../views/data/data-path.js';
import { isProduction, showDevTools } from '../helpers/app-mode.js';
import { NSDirectoryService, menuItem } from '../ns-services/ns-directory-service.js';
import { NSLoader } from '../ns-services/ns-loader-service.js';
import { NSCommuncationService } from '../ns-services/ns-communication-service.js';
import { PublicLedgerService } from '../ns-services/ns-public-ledger-service.js';
import { NSTrustScoreService } from '../ns-services/ns-trust-score-service.js';
import { NSStripeService } from '../ns-services/ns-stripe-service.js';
import { jobErrorStore } from '../ns-services/ns-job-error-store.js';

// Components
import '@buttress/crag/components/buttress-db-service.js';
import '../ltn-auth/ltn-auth-service.js';
import '../ns-services/ns-directory-service.js';
import '../ltn-router/ltn-router.js';
import '../ns-settings/ns-settings-service.js';
import '../ltn-toast/ltn-toast-service.js';
import '../ltn-dialog/ltn-dialog-service.js';
import '../ns-services/ns-communication-service.js';
import '../ns-services/ns-public-ledger-service.js';
import '../ns-services/ns-trust-score-service.js';
import '../ns-services/ns-stripe-service.js';
import '../ns-workflows/ns-workflow-service.js';
import '../ns-tutorial/ns-tutorial-service.js';
import '../ns-services/ns-error-directory.js';

import '@awesome.me/webawesome/dist/styles/themes/default.css';
import '@awesome.me/webawesome/dist/components/button/button.js';

// Material Design Elements

// Dev Components
import '../ns-elements/ns-top-bar.js';
import '../ns-elements/ns-side-bar.js';
import '../ns-icon/ns-icon.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import type WaInput from '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/otp-input/otp-input.js';
import type WaOtpInput from '@awesome.me/webawesome/dist/components/otp-input/otp-input.js';
import '../ns-animation/ns-main-logo.js';
import '../ns-services/ns-loader-service.js';
import '../ns-start-menu/ns-start-menu.js';
import '../ns-background-tasks/ns-background-tasks.js';
import '../ns-dev-tools/ns-dev-tools-panel.js';
import '../ns-notifications/ns-notification-dropdown.js';

import { NSStartMenu } from '../ns-start-menu/ns-start-menu.js';
import { NSBackgroundTasks } from '../ns-background-tasks/ns-background-tasks.js';
import { NSDevToolsPanel } from '../ns-dev-tools/ns-dev-tools-panel.js';
import { NSNotificationDropdown } from '../ns-notifications/ns-notification-dropdown.js';

import '../ns-services/ns-email-service.js';
import { callLambda } from '../helpers/lambda.js';
import { holdOAuthLanding, readOAuthLanding, referrerPolicyFor, withoutQuery } from '../views/oauth/oauth-landing.js';
import { connectWithRetry } from '../helpers/db-connection.js';
import { createLogger, getLogLevel } from '../ns-services/logger.js';

const logger = createLogger('ns-app');

reduceWebAnimations();

const EMAIL_CODE_REJECTED = 401;

enum AuthState {
  AUTH_REQUIRED = LtnAuthState.AUTH_REQUIRED,
  AUTHENTICATED = LtnAuthState.AUTHENTICATED,
  LOADING = LtnAuthState.LOADING,
  ERROR = LtnAuthState.ERROR,
  EMAIL_AUTH = 4,
  EMAIL_AUTH_CHECK = 5,
  REQUEST_INVITATION = 6,
  EMAIL_LINK = 7,
}

interface AuthData {
  invitation: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  companyName: string;
  registrarIdentifier: string;
}
@customElement('ns-app')
export class NSApp extends LitElement {
  private __bjsSettings?: Partial<Settings>;

  // Created here, not in the template: the template's own children ask for these as they connect.
  private __db = document.createElement('buttress-db-service') as ButtressDbService;

  // The reads every view makes through the shared db service, so the page load's shutter can wait for the opened view's
  // data (__openBootLoader)
  private __dbReads = new DbReadActivity();

  private __dbProvider = new ContextProvider(this, { context: buttressDbServiceContext, initialValue: this.__db });

  private __router = document.createElement('ltn-router') as LtnRouter;

  private __routerProvider = new ContextProvider(this, { context: ltnRouterContext, initialValue: this.__router });

  private __routeProvider = new ContextProvider(this, { context: ltnRouteContext });

  private __settings = document.createElement('ns-settings-service') as NSSettingsService;

  private __settingsServiceProvider = new ContextProvider(this, {
    context: nsSettingsServiceContext,
    initialValue: this.__settings,
  });

  private __settingsProvider = new ContextProvider(this, { context: nsSettingsContext });

  private __auth = document.createElement('ltn-auth-service') as LtnAuthService;

  private __authProvider = new ContextProvider(this, { context: ltnAuthServiceContext, initialValue: this.__auth });

  private __authStateProvider = new ContextProvider(this, {
    context: ltnAuthStateContext,
    initialValue: { state: this.__auth.state },
  });

  private __dir = document.createElement('directory-service') as NSDirectoryService;

  private __dirProvider = new ContextProvider(this, { context: nsDirectoryServiceContext, initialValue: this.__dir });

  private __dirStateProvider = new ContextProvider(this, { context: nsDirectoryStateContext });

  private __toast = document.createElement('ltn-toast-service') as LtnToastService;

  private __toastProvider = new ContextProvider(this, { context: ltnToastServiceContext, initialValue: this.__toast });

  private __dialog = document.createElement('ltn-dialog-service') as LtnDialogService;

  private __dialogProvider = new ContextProvider(this, {
    context: ltnDialogServiceContext,
    initialValue: this.__dialog,
  });

  private __loader = document.createElement('ns-loader') as NSLoader;

  private __loaderProvider = new ContextProvider(this, { context: nsLoaderContext, initialValue: this.__loader });

  private __communcation = document.createElement('ns-communcation-service') as NSCommuncationService;

  private __communcationProvider = new ContextProvider(this, {
    context: nsCommuncationServiceContext,
    initialValue: this.__communcation,
  });

  private __publicLedger = document.createElement('public-ledger-service') as PublicLedgerService;

  private __publicLedgerProvider = new ContextProvider(this, {
    context: publicLedgerServiceContext,
    initialValue: this.__publicLedger,
  });

  private __trustScores = document.createElement('trust-score-service') as NSTrustScoreService;

  private __trustScoresProvider = new ContextProvider(this, {
    context: nsTrustScoreServiceContext,
    initialValue: this.__trustScores,
  });

  private __stripe = document.createElement('ns-stripe-service') as NSStripeService;

  private __stripeProvider = new ContextProvider(this, {
    context: nsStripeServiceContext,
    initialValue: this.__stripe,
  });

  private __workflows = document.createElement('ns-workflow-service') as NSWorkflowService;

  private __workflowsProvider = new ContextProvider(this, {
    context: nsWorkflowServiceContext,
    initialValue: this.__workflows,
  });

  private __tutorial = document.createElement('ns-tutorial-service') as NSTutorialService;

  private __tutorialProvider = new ContextProvider(this, {
    context: nsTutorialServiceContext,
    initialValue: this.__tutorial,
  });

  private __debounceLoadAnimation?: Debouncer;

  @query('error-directory')
  private __errorDirectory?: ErrorDirectory;

  private __createFormController: FinalFormController<FormValues>;

  private __errors: Partial<Record<keyof FormValues, string>> = {};

  @state()
  private __requestedInvitationSent: boolean = false;

  @state()
  private __requestedInvitationError?: string;

  @state()
  private __validateTokenURL: string = '%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/auth/user';

  @state()
  private __authenticateURL: string = '%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/google/auth';

  private __authenticateEmailURL: string = '%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/auth/email';
  private __authenticateEmailCheckURL: string = '%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/auth/email-check';
  private __authenticateEmailLinkURL: string = '%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/auth/email-link';

  private __authenticateEmail?: string;
  private __emailChallengeToken?: string;
  private __emailLinkToken: string | null = null;

  @state()
  private __emailLinkFailed = false;

  @state()
  private __themeMode: string = 'light';

  @state()
  @state()
  private __isStartMenuOpen: boolean = false;

  @state()
  private __isTasksMenuOpen: boolean = false;

  @state()
  private __isDevToolsOpen: boolean = false;

  @state()
  private __isNotificationsOpen: boolean = false;

  // Unknown until the first count, so the badge doesn't pulse as if notifications had just arrived
  @state()
  private __unreadNotificationCount?: number;

  @state()
  private __onboarding: boolean = false;

  @property({ attribute: false })
  private __dbLoading: boolean = false;

  // The page load's closed shutter has been opened
  private __bootLoaderOpened = false;

  private __dbConnecting?: Promise<void>;

  @property({ attribute: false })
  private __authState: AuthState = AuthState.LOADING;

  @property({ attribute: false })
  private __view: string = 'feed';

  // This page load is an HMRC or Companies House sign-in landing (/oauth/<provider>), whose code is held in memory only
  private __oauthLanding = false;

  @property({ attribute: false })
  private __isMobile: boolean = false;

  @property({ attribute: false })
  private __app?: SettingsObject;

  private get __isProduction(): boolean {
    return isProduction((this.__settings.settings.app as SettingsObject | undefined)?.mode as string | undefined);
  }

  private __onPermissionsChanged() {
    this.requestUpdate();
    if (this.__router.route) this.__routeChange(this.__router.route);
  }

  private __getSideBarTopMenu(): menuItem[] {
    const studios: menuItem[] = [
      ...(this.__dir.can('DATA_MANAGE') || this.__dir.can('DATA_SHARING_MANAGE')
        ? [{ id: 'data', title: 'Data', icon: 'data', link: '/data' }]
        : []),
      ...(this.__dir.can('WORKFLOWS_MANAGE')
        ? [{ id: 'workflow-studio', title: 'Workflow Studio', icon: 'step-rule', link: '/workflow-studio' }]
        : []),
    ];
    return [...this.__sideBarTopMenu, ...studios];
  }

  // Replaced, not mutated, so the side bar sees the change
  @state()
  private __sideBarTopMenu: menuItem[] = [
    {
      id: 'feed',
      title: 'Feed',
      icon: 'feed',
      link: '/feed',
    },
    {
      id: 'workflows',
      title: 'Workflows',
      icon: 'workflow',
      link: '/workflows',
    },
    {
      id: 'connections',
      title: 'Supply Chain',
      icon: 'connections',
      link: '/connections',
    },
    {
      id: 'org-chart',
      title: 'Org Chart',
      icon: 'org-chart',
      link: '/org-chart',
    },
    // {
    //   id: 'companies',
    //   title: 'Companies',
    //   icon: 'search',
    //   link: '/companies',
    // },
    {
      id: 'relationships',
      title: 'Relationships',
      icon: 'graph',
      link: '/relationships',
    },
  ];

  private __getSideBarBottomMenu(): menuItem[] {
    const devTools = showDevTools(this.__app?.mode as string | undefined);

    return [
      {
        id: 'notifications',
        title: 'Notifications',
        icon: 'notification',
        link: '/',
        fn: async () => this.__openStartMenu('notifications'),
        badgeCount: this.__unreadNotificationCount,
      },
      {
        id: 'launcher',
        title: 'Workflow Launcher',
        icon: 'launch-pad',
        link: '/',
        fn: async () => this.__openStartMenu('launcher'),
      },
      // {
      //   id: 'background-tasks',
      //   title: 'Background Tasks',
      //   icon: 'task',
      //   link: '/',
      //   fn: async () => this.__openStartMenu('background-tasks'),
      // },
      {
        id: 'my-account',
        title: 'My Account',
        icon: 'account',
        link: '/my-account',
      },
      ...(devTools
        ? [
            {
              id: 'dev-tools',
              title: 'Dev Tools',
              icon: 'warning',
              link: '/',
              fn: async () => this.__openStartMenu('dev-tools'),
            },
          ]
        : []),
      {
        id: 'logout',
        title: 'Logout',
        icon: 'logout',
        link: '/',
        fn: async () => this.__logout(),
      },
    ];
  }

  private __sideBarActiveItems: string[] = [];

  private readonly __views: {
    [index: string]: {
      title: string;
      import?: Function;
    };
  } = {
    feed: {
      title: 'Feed',
      import: () => import('../views/feed/view-feed.js'),
    },
    workflows: {
      title: 'Workflows',
      import: () => import('../views/workflows/view-workflows.js'),
    },
    company: {
      title: 'Company',
      import: () => import('../views/company/view-company.js'),
    },
    'my-account': {
      title: 'My Account',
      import: () => import('../views/account/view-account.js'),
    },
    onboarding: {
      title: 'Onboarding',
      import: () => import('../views/onboarding/view-onboarding.js'),
    },
    connections: {
      title: 'Supply Chain',
      import: () => import('../views/connections/view-connections.js'),
    },
    'org-chart': {
      title: 'Org Chart',
      import: () => import('../views/org-chart/view-org-chart.js'),
    },
    notifications: {
      title: 'Notifications',
      import: () => import('../views/notifications/view-notifications.js'),
    },
    companies: {
      title: 'Companies',
      import: () => import('../views/companies/view-companies.js'),
    },
    relationships: {
      title: 'Relationships',
      import: () => import('../views/relationships/view-relationships.js'),
    },
    data: {
      title: 'Data',
      import: () => import('../views/data/view-data.js'),
    },
    'workflow-studio': {
      title: 'Workflow studio',
      import: () => import('../views/workflow-studio/view-workflow-studio.js'),
    },
    oauth: {
      title: 'Connecting',
      import: () => import('../views/oauth/view-oauth.js'),
    },
  };

  private get __anyOverlayOpen(): boolean {
    return this.__isStartMenuOpen || this.__isTasksMenuOpen || this.__isDevToolsOpen || this.__isNotificationsOpen;
  }

  static styles = [
    nsLayoutStyles,
    nsAnimationStyles,
    nsColours,
    nsLightTheme,
    nsSharedStyles,
    nsCardStyles,
    css`
      .fit {
        position: absolute;
        inset: 0;
      }

      :host {
        display: block;
      }

      ns-side-bar {
        z-index: 998;
      }

      /* The signed-in shell is the window: views scroll inside their own panels, so nothing in it (a card growing as it
         expands) may reach past it and give the page a scrollbar */
      #main-container {
        position: relative;
        height: 100%;
        padding: var(--ns-theme-viewport-padding, var(--wa-space-2xl));
        box-sizing: border-box;
        overflow: clip;
      }

      #app-container {
        height: 100vh; /* We need to set this to the height of the screen so we can size the application views correctly */
        background-color: var(--primary-background-color, --primary-color);
      }

      #app-content {
        --side-bar-clearance: 95px;

        width: calc(100% - 75px);
        margin: 0 0 0 var(--side-bar-clearance);
      }

      #view-container {
        flex: 1;
        height: 100%;
        min-height: 0;
      }

      #view-container > * {
        height: 100%;
        animation: ns-fade-in var(--ns-motion-duration) var(--ns-motion-easing);
      }

      .page-not-found {
        background: var(--ns-theme-background);
        border-radius: var(--wa-border-radius-l);
        color: var(--ns-theme-type);
      }

      .page-not-found ns-icon {
        --icon-width: 96px;
        --icon-height: 96px;
      }

      .page-not-found__code {
        font-size: var(--wa-font-size-s);
        font-weight: 600;
        letter-spacing: 0.08em;
        color: var(--ns-theme-red);
      }

      .page-not-found__title {
        font-size: var(--wa-font-size-xl);
        font-weight: 700;
      }

      .page-not-found__subtitle {
        font-size: var(--wa-font-size-s);
        color: var(--ns-theme-grey-dark);
        text-align: center;
        max-width: 320px;
      }

      #mainMenu .menu-content {
        padding: var(--wa-space-m) 0;
        box-sizing: border-box;
      }

      #mainMenu .menu-content .overline {
        padding: 0 var(--wa-space-m);
      }

      .app-login {
        background: var(--wa-color-brand-fill-loud);
      }

      .app-login,
      .request-invitation-page {
        padding: 0 var(--wa-space-m);
        width: 600px;
      }

      .login-image {
        position: absolute;
        right: 0;

        background: var(--wa-color-brand-fill-loud) url('/images/login/login-image.png') no-repeat center center / cover;
        width: calc(100% - 600px);
      }

      .footer {
        color: var(--wa-color-text-normal);
      }

      .app-login .footer,
      .login-or,
      .app-auth-error {
        color: var(--ns-theme-white);
      }

      .app-auth-error__title {
        margin: 0 0 var(--wa-space-xs);
        font-size: var(--wa-font-size-xl);
      }

      .app-auth-error__desc {
        margin: 0 0 var(--wa-space-l);
      }

      .login-buttons .google-logo,
      .login-buttons .microsoft-logo {
        width: 20px;
      }

      wa-button.auth-back {
        position: absolute;
        top: var(--wa-space-m);
        left: var(--wa-space-m);
        z-index: 1;
        --icon-width: 3em;
        --icon-height: 3em;
        color: var(--ns-theme-white);
      }

      .card {
        height: 100%;
        width: 35vw;

        min-width: 300px;
        max-width: 500px;
      }

      .auth-email-card .stage-card {
        background-color: var(--wa-color-surface-default);
        width: 400px;
        padding: var(--wa-space-m);
        border-radius: var(--wa-border-radius-l);
      }

      ns-start-menu {
        bottom: var(--ns-theme-viewport-padding, 40px);
        max-height: 500px;
        border-radius: var(--wa-border-radius-l);
        width: 100%;
        max-width: 900px;
        overflow: hidden;
      }

      ns-notification-dropdown {
        /* No explicit left - matches ns-start-menu's own static-position anchor (flush against
           this same .layout.vertical.h-100 container), so both overlays open from the same spot. */
        bottom: var(--ns-theme-viewport-padding, 40px);
        width: 380px;
        max-height: 480px;
        border-radius: var(--wa-border-radius-l);
        overflow: hidden;
      }

      .menu-backdrop {
        position: fixed;
        background: transparent;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;

        z-index: 998;

        visibility: hidden;
        pointer-events: none;
      }

      .menu-backdrop[open] {
        visibility: visible;
        pointer-events: auto;
        cursor: pointer;
      }

      .request-invitation-page {
        background: var(--wa-color-brand-fill-loud);
      }

      .request-an-invitation {
        min-height: 300px;
        min-width: 500px;

        background: var(--wa-color-surface-default);

        padding: var(--wa-space-m);

        border-radius: var(--wa-border-radius-l);
      }

      .request-an-invitation.green {
        background: var(--wa-color-success-fill-loud);
      }
    `,
  ];

  constructor() {
    super();
    // A page load that will read the database (signed in, so it connects and its view reads) or finishes a sign-in starts
    // behind the closed shutter, which opens once there is something to show (__openBootLoader). Anything else (the login
    // page, an invitation or email link) has nothing to wait for and shows straight away.
    if (NSApp.__startsBehindShutter(new URL(window.location.href))) {
      this.__loader.startClosed();
    } else {
      this.__loader.classList.add('hide');
      this.__bootLoaderOpened = true;
    }
    this.__db.query = this.__dbReads.wrap(this.__db.query.bind(this.__db));
    this.__db.getById = this.__dbReads.wrap(this.__db.getById.bind(this.__db)) as ButtressDbService['getById'];
    this.__db.count = this.__dbReads.wrap(this.__db.count.bind(this.__db));
    this.__communcation.append(document.createElement('ns-email-service'));

    this.__createFormController = new FinalFormController(this, {
      onSubmit: async (values) => await this.__submitRequestAnInvitation(values),
      validate: () => {
        const errors: Partial<Record<keyof FormValues, string>> = {};
        return errors;
      },
    } as FormConfig<FormValues>);
  }

  async connectedCallback() {
    const url = new URL(window.location.href);
    // A sign-in landing: the code and state come off the address bar before anything else runs or is fetched, and are
    // held in memory for view-oauth, which posts them once
    const oauthLanding = readOAuthLanding(url);
    const referrerPolicy = referrerPolicyFor(oauthLanding);
    if (oauthLanding && referrerPolicy) {
      // No request from this page names the landing URL, which carried the code
      const meta = document.createElement('meta');
      meta.name = 'referrer';
      meta.content = referrerPolicy;
      document.head.prepend(meta);
      holdOAuthLanding(oauthLanding);
      window.history.replaceState({}, '', withoutQuery(url));
      this.__oauthLanding = true;
      import('../views/oauth/view-oauth.js');
    }
    this.__emailLinkToken = emailLinkToken(url);
    if (this.__emailLinkToken) window.history.replaceState({}, '', '/');

    // A sign-in landing's `error` is the provider's (view-oauth reads it), not one for a card or a toast
    const error = oauthLanding ? null : url.searchParams.get('error');
    let appError: string | undefined;
    if (error) {
      const jobId = url.pathname.split('/').pop();
      if (jobId) {
        try {
          jobErrorStore.set(jobId, JSON.parse(base64UrlDecode(error)));
        } catch {
          // Malformed payload - nothing sensible to store, just drop it.
        }
      } else {
        appError = error;
      }
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
    }

    super.connectedCallback();
    await this.updateComplete;
    if (appError) this.__toast.queueToast({ label: appError, variant: 'danger', timeout: 6000 });

    // Register listeners for this component
    // window.addEventListener('page-title', (ev: Event) => this.__setPageTitle(ev as CustomEvent));
    window.addEventListener('view-path', (ev: Event) => this.__viewPath(ev as CustomEvent));
    window.addEventListener('load-login-logo', () => this.__loadLogo());
    window.addEventListener('onboarding-complete', () => this.__onboardingComplete());
    window.addEventListener('resize', () => this.__handleWindowReize());

    window.addEventListener('logout', () => this.__logout());

    window.addEventListener('keydown', (ev: KeyboardEvent) => {
      if (
        ev.key === 'Escape' &&
        (this.__isStartMenuOpen || this.__isTasksMenuOpen || this.__isDevToolsOpen || this.__isNotificationsOpen)
      ) {
        this.__closeStartMenu();
      }
    });
    window.addEventListener('close-start-menu', () => this.__closeStartMenu());
    window.addEventListener('launch-workflow', (ev: Event) => {
      const workflowId = (ev as CustomEvent).detail?.workflowId;
      if (!workflowId) return;
      viewPath(`/workflows/${workflowId}`);
    });

    this.__handleWindowReize();
    try {
      await this.__wireServices();
    } catch (err) {
      logger.error('Unable to start the app', err);
      this.__authState = AuthState.ERROR;
    }

    window.addEventListener('pageshow', async (event) => {
      if (event.persisted) {
        logger.info('Page restored from bfcache');
        await this.__auth?.validateToken();
        this.__clearRejectedStoredToken();
        await this.__loader?.finishLoading();
      }
    });
  }

  willUpdate(changedProperties: PropertyValues) {
    // The auth service is created up front, so these are no longer template bindings on it.
    if (changedProperties.has('__validateTokenURL')) this.__auth.validateTokenURL = this.__validateTokenURL;
    if (changedProperties.has('__authenticateURL')) this.__auth.authenticateURL = this.__authenticateURL;
    // nsLightTheme's roles apply under [theme='light']; onboarding keeps its own design
    this.setAttribute('theme', this.__themeMode);
  }

  updated(changedProperties: PropertyValues) {
    void this.__openBootLoader();

    if (
      changedProperties.has('__view') ||
      changedProperties.has('__authState') ||
      changedProperties.has('__onboarding')
    ) {
      if (this.__authState !== AuthState.AUTHENTICATED) {
        document.title = 'Login - nodeStream';
      } else if (this.__onboarding) {
        document.title = 'Onboarding - nodeStream';
      } else {
        const viewTitle = this.__views[this.__view]?.title;
        document.title = viewTitle ? `${viewTitle} - nodeStream` : 'nodeStream';
      }
    }
  }

  private static __startsBehindShutter(url: URL) {
    if (readOAuthLanding(url)) return true;
    if (url.searchParams.get('userToken')) return true;
    try {
      return !!sessionStorage.getItem('userToken');
    } catch {
      return false;
    }
  }

  // Opens the page load's shutter once the app has something to show: the sign-in is known and, when signed in, the
  // database is connected (or onboarding has taken over), and the view that opened has its data: its database reads have
  // settled, however long they take (a view that reads nothing opens straight away). An HMRC or Companies House landing
  // keeps it closed: view-oauth opens it only to show an error, and otherwise goes on to the card, whose page load starts
  // closed again.
  private async __openBootLoader() {
    if (this.__bootLoaderOpened || this.__oauthLanding) return;
    if (this.__authState === AuthState.LOADING) return;
    if (this.__authState === AuthState.AUTHENTICATED && this.__dbLoading && !this.__onboarding) return;
    this.__bootLoaderOpened = true;
    await this.updateComplete;
    await this.__dbReads.whenSettled();
    await this.__loader.finishLoading();
  }

  private async __wireServices() {
    // Before validateToken below, since that is what sets the directory initialising.
    this.__dir.addEventListener('initialised', () => this.__publishDirectory());
    this.__dir.addEventListener('profile-change', () => this.__publishDirectory());
    this.__dir.addEventListener('company-change', () => this.__publishDirectory());
    // What the caller may do decides which views open and which sidebar items show
    this.__dir.addEventListener('initialised', () => this.__onPermissionsChanged());
    this.__dir.addEventListener('me-updated', () => this.__onPermissionsChanged());
    // Another tab switched company: this one closes the shutter, then reloads where it is
    this.__dir.addEventListener('active-profile-switched', (ev) => {
      ev.preventDefault();
      void reloadBehindShutter(this.__loader, 'Switching company…', window.location.href);
    });
    this.__dir.addEventListener('initialise-failed', () => {
      this.__authState = AuthState.ERROR;
    });

    this.__router.addEventListener('route-changed', () => this.__publishRoute());
    this.__publishRoute();

    this.__settings.addEventListener('settings-updated', () => this.__publishSettings());
    this.__publishSettings();

    this.__db.addEventListener('bjs-connection-changed', (ev: Event) => {
      logger.debug('bjs-connection-changed', ev);
    });

    this.__auth.addEventListener('auth-changed', (ev: Event) => this.__publishAuthState((ev as CustomEvent).detail));

    const errorCode = await this.__auth.authErrors();
    if (errorCode) {
      if (!this.__errorDirectory) throw new Error('Can not find error directory service');

      this.__errorDirectory.errorCode = Number(errorCode);
      if (!this.__errorDirectory.errorMessage) throw new Error('Can not get error directory message');

      this.__toast?.queueToast({
        label: this.__errorDirectory.errorMessage,
        timeout: 4000,
      });

      return;
    }

    await this.__auth.validateToken();
    this.__clearRejectedStoredToken();

    // const google = this._queryService(LtnGoogleService);
    // if (google) {
    //   trader.registerService({name: 'google', service: google });
    //   this._debug('Registered google with trader');

    //   if (cloud) cloud.registerService('google', google as CloudService);
    //   this._debug('Registered google with cloud service');
    // }

    this.__subscribeUnreadNotifications();
  }

  // Always-on, independent of the notification dropdown's own lazy load-on-open subscription
  // (client/src/ns-notifications/ns-notification-dropdown.ts) — the sidebar badge has to be
  // accurate even while the dropdown has never been opened.
  private async __subscribeUnreadNotifications() {
    if (!this.__db || !this.__dir) return;

    const openedAt = Date.now();
    await this.__dir.initialised;
    const myPersonId = this.__dir.me?.id;
    if (!myPersonId) return;

    const computeCount = async () => {
      if (!this.__db) return;
      this.__unreadNotificationCount = await this.__db.count('notification', {
        personId: { $eq: myPersonId },
        readAt: { $eq: null },
      });
    };

    this.__db.subscribe('notification.*', (change: { path?: string }) => {
      computeCount();
      this.__toastArrival(change, myPersonId, openedAt);
    });
    this.__db.addEventListener('bjs-resync', () => computeCount());
    computeCount();
  }

  // A whole-record change is a new notification; a deeper path is one being read or delivered
  // Onboarding gets no toasts: the jobs its answers create would pop up over the questions. The
  // notifications are still kept, and the notifications list shows them once the user is in the app.
  private async __toastArrival(change: { path?: string }, personId: string, since: number) {
    const [, id, ...deeper] = change.path?.split('.') ?? [];
    if (!id || deeper.length > 0 || !this.__db || this.__onboarding) return;
    const notification = await this.__db.getById<Notification>('notification', id);
    if (!notification || !isFreshArrival(notification, personId, since) || this.__onboarding) return;

    const path = notificationPath(notification);
    const open = path
      ? () => {
          markNotificationsRead(this.__db, [notification]);
          viewPath(path);
        }
      : null;
    this.__toast.queueToast(notificationToast(notification, open));
  }

  private __publishRoute() {
    this.__routeProvider.setValue(this.__router.route);
    this.__routeChange(this.__router.route);
  }

  private __routeChange(route: Route) {
    let [root] = route.path.split('/').filter((n) => n);

    if (!root) root = 'feed';

    logger.debug(`Root changed : ${root}`);
    if (this.__views[root] && this.__routeOpen(root, route.path)) {
      this.__view = root;
      this.__views[root].import?.();
    } else {
      this.__view = '404';
    }

    this.__onboarding = root === 'onboarding';
    this.__routeTutorial();
  }

  // The guide runs on onboarding, and on the app's views only once the user is signed in (a signed-out
  // `/` is the login page, with no feed to tour)
  private __routeTutorial() {
    const authenticated = this.__authState === AuthState.AUTHENTICATED;
    this.__tutorial.routeChanged(this.__onboarding ? 'onboarding' : authenticated ? this.__view : '');
  }

  // Data Studio and Workflow Studio's editing are features a company may not have (#12 D16, D25); the server still decides
  private __routeOpen(root: string, path: string) {
    if (root !== 'data') return true;
    const holds = this.__dir.can('DATA_MANAGE') || this.__dir.can('DATA_SHARING_MANAGE');
    return dataScreenOpen(parseDataPath(path), holds);
  }

  private __publishDirectory() {
    this.__dirStateProvider.setValue({ me: this.__dir.me, company: this.__dir.company, people: this.__dir.people });
  }

  private __publishAuthState(state: LtnAuthState) {
    this.__authStateProvider.setValue({ state });
    this.__authStateChanged(state as number);
  }

  private async __authStateChanged(state: AuthState) {
    this.__authState = state === AuthState.AUTH_REQUIRED && this.__emailLinkToken ? AuthState.EMAIL_LINK : state;
    this.__routeTutorial();

    if (!this.__auth) throw new Error('Can not find auth service');

    if (this.__authState === AuthState.AUTH_REQUIRED) {
      this.__loadLogo();
    }

    if (!this.__db) throw new Error('Can not find db service');
    if (!this.__bjsSettings) throw new Error('Can not find settings service');

    if (this.__auth.token && this.__bjsSettings.token !== this.__auth.token) {
      this.__bjsSettings.token = this.__auth.token;
      this.__bjsSettings.userId = this.__auth.user?.id;
      this.__db.setToken(this.__bjsSettings.token);
      if (this.__bjsSettings.userId) this.__db.setUserId(this.__bjsSettings.userId);
      this.__syncDbSettings();
    }

    if (this.__authState === AuthState.AUTHENTICATED && !this.__db.isDbConnected()) {
      this.__dbLoading = true;

      await this.__connectDb();

      // TODO: Populate invitation token from the auth response
      // const invitiationToken = this.__auth.data.invitationToken;
      const data = this.__auth.data as AuthData;
      const invitiationToken = data?.invitation;

      if (!(await this.__onboardingRedirect(invitiationToken))) {
        this.__dbLoading = false;
      }
    }
  }

  private async __loadLogo() {
    this.__debounceLoadAnimation = Debouncer.debounce(this.__debounceLoadAnimation, timeOut.after(50), async () => {
      await this.updateComplete;
      const mainLogo = this.renderRoot.querySelector('ns-main-logo');
      mainLogo?.dispatchEvent(
        new CustomEvent('start-animation', {
          bubbles: true,
          composed: true,
        }),
      );
    });
  }

  private async __restartLoadingAnimation() {
    if (!this.__loader) throw new Error('Can not find loader service - __restartLoadingAnimation');

    const selectedOption = this.renderRoot.querySelector('input[name="loading-mode"]:checked');
    const animationFunction = selectedOption?.getAttribute('value');
    await this.__loader.startLoading(animationFunction ?? undefined);
    await this.__loader.finishLoading(animationFunction ?? undefined);
  }

  private async __onboardingRedirect(invitationToken?: string) {
    if (!this.__router || !this.__router.route) throw new Error('Can not find router service');
    if (!invitationToken) return false;

    const [root] = this.__router.route.path.split('/').filter((v) => v);
    if (root && root.toUpperCase() === 'ONBOARDING') return false;

    // Replaces the page signed in at rather than pushing past it, so Back can't return to the app before
    // onboarding is finished
    window.history.replaceState({}, '', `/onboarding?invitation=${invitationToken}`);
    window.dispatchEvent(new CustomEvent('location-changed'));
  }

  private __handleWindowReize() {
    if (window.innerWidth < 600 && !this.__isMobile) {
      this.__isMobile = true;
    } else if (window.innerWidth >= 600 && this.__isMobile) {
      this.__isMobile = false;
    }
  }

  // private __setPageTitle(ev: CustomEvent) {
  //   const path: string = ev.detail || ev;
  //   // TODO: Update this to use the view titles instead of hardcoding it here.
  // }

  private __viewPath(ev: CustomEvent) {
    const path = ev.detail || ev;

    window.history.pushState({}, '', path);
    window.dispatchEvent(new CustomEvent('location-changed'));
  }

  private async __onboardingComplete() {
    if (!this.__loader) throw new Error('Can not find loader service - __onboardingComplete');

    this.__dbLoading = false;
    // Fire settings update as we've switched theme and it will kick it to update it.
    this.__settingsUpdate();
    await this.__loader.finishLoading();
  }

  private __publishSettings() {
    applyReduceMotionSetting(this.__settings.settings.reduceMotion === true);
    this.__settingsProvider.setValue(this.__settings.settings);
    this.__settingsUpdate();
  }

  private __settingsUpdate() {
    if (!this.__settings || !this.__settings.settings.app) return;

    // App settings
    this.__app = this.__settings.settings.app as SettingsObject;

    // Buttress settings
    if (!this.__bjsSettings) this.__bjsSettings = {};
    const bjsSttings = this.__settings.settings.buttress as SettingsObject;
    this.__bjsSettings = {
      ...this.__bjsSettings,
      endpoint: bjsSttings.endpoint as string,
      apiPath: bjsSttings.apiPath as string,
    };
    this.__syncDbSettings();

    this.__updateThemeMode('light');

    // Google Service
  }

  private __connectDb(): Promise<void> {
    this.__dbConnecting ??= connectWithRetry(this.__db, (err, attempt) => {
      logger.warn(`Unable to connect to Buttress (attempt ${attempt})`, err);
      if (attempt === 1) this.__toast?.queueToast({ label: "Can't reach the server. Retrying…", timeout: 6000 });
    }).finally(() => {
      this.__dbConnecting = undefined;
    });
    return this.__dbConnecting;
  }

  private __syncDbSettings() {
    Object.assign(this.__db, {
      logLevel: getLogLevel('buttress-db-service'),
      endpoint: this.__bjsSettings?.endpoint,
      token: this.__bjsSettings?.token,
      apiPath: this.__bjsSettings?.apiPath,
      userId: this.__bjsSettings?.userId,
    });
  }

  private async __openStartMenu(menu: string) {
    if (menu === 'launcher') {
      const startMenu = this.shadowRoot?.querySelector('ns-start-menu') as NSStartMenu;
      if (!startMenu) throw new Error('Can not find start menu element');
      this.__isStartMenuOpen = !this.__isStartMenuOpen;
      this.__sideBarActiveItems = [...this.__sideBarActiveItems, 'launcher'];
      setTimeout(() => startMenu.focusInput(), 100);
    }
    if (menu === 'background-tasks') {
      const backgroundTasksList = this.shadowRoot?.querySelector('ns-background-tasks') as NSBackgroundTasks;
      if (!backgroundTasksList) throw new Error('Can not find background tasks list element');
      this.__isTasksMenuOpen = !this.__isTasksMenuOpen;
      this.__sideBarActiveItems = [...this.__sideBarActiveItems, 'background-tasks'];

      await this.updateComplete;
      backgroundTasksList.focusPanel();
    }
    if (menu === 'dev-tools') {
      const devToolsPanel = this.shadowRoot?.querySelector('ns-dev-tools-panel') as NSDevToolsPanel;
      if (!devToolsPanel) throw new Error('Can not find dev tools panel element');
      this.__isDevToolsOpen = !this.__isDevToolsOpen;
      this.__sideBarActiveItems = [...this.__sideBarActiveItems, 'dev-tools'];
    }
    if (menu === 'notifications') {
      const notificationDropdown = this.shadowRoot?.querySelector('ns-notification-dropdown') as NSNotificationDropdown;
      if (!notificationDropdown) throw new Error('Can not find notification dropdown element');
      this.__isNotificationsOpen = !this.__isNotificationsOpen;
      this.__sideBarActiveItems = [...this.__sideBarActiveItems, 'notifications'];
    }
  }

  private __closeStartMenu() {
    const startMenu = this.shadowRoot?.querySelector('ns-start-menu') as NSStartMenu;
    if (!startMenu) throw new Error('Can not find start menu element');

    const backgroundTasksList = this.shadowRoot?.querySelector('ns-background-tasks') as NSBackgroundTasks;
    if (!backgroundTasksList) throw new Error('Can not find background tasks list element');

    this.__isStartMenuOpen = false;
    this.__isTasksMenuOpen = false;
    this.__isDevToolsOpen = false;
    this.__isNotificationsOpen = false;
    this.__sideBarActiveItems = this.__sideBarActiveItems.filter(
      (item) => item !== 'launcher' && item !== 'background-tasks' && item !== 'dev-tools' && item !== 'notifications',
    );

    startMenu.clear();
  }

  private async __switchOnboardingTheme(mode: string) {
    if (!this.__router || !this.__router.route) throw new Error('Can not find router service');
    const [root] = this.__router.route.path.split('/').filter((n) => n);
    if (!root) return;
    this.__themeMode = root === 'onboarding' ? 'onboarding' : mode;
  }

  private async __updateThemeMode(mode: string) {
    this.__themeMode = mode;

    this.__switchOnboardingTheme(mode);
    logger.debug('update theme mode', mode);

    const style = this.shadowRoot?.querySelector('#theme_styles');
    if (!style) return;

    const isDifferent = style.getAttribute('href')?.includes(`${this.__themeMode}.css`) === false;
    if (!isDifferent) return;

    style.setAttribute('href', `/themes/${this.__themeMode}.css`);
  }

  private async __googleLogin() {
    if (!this.__loader) throw new Error('Can not find loader service');
    await this.__loader.startLoading();

    this.__authenticateURL = '%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/google/auth';
    await this.updateComplete;
    await this.__auth?.authenticate();
  }

  private async __microsoftLogin() {
    if (!this.__loader) throw new Error('Can not find loader service');
    await this.__loader.startLoading();

    this.__authenticateURL = '%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/microsoft/auth';
    await this.updateComplete;
    this.__auth?.authenticate();
  }

  private async __emailLogin() {
    // if (!this.__loader) throw new Error('Can not find loader service');
    // await this.__loader.startLoading();

    this.__authState = AuthState.EMAIL_AUTH;

    // await this.__loader.finishLoading();
  }

  private __backToLoginOptions() {
    this.__requestedInvitationSent = false;
    this.__requestedInvitationError = undefined;
    this.__authState = AuthState.AUTH_REQUIRED;
    this.__loadLogo();
  }

  private __backToEmailEntry() {
    this.__authState = AuthState.EMAIL_AUTH;
  }

  private __leaveEmailLink() {
    this.__emailLinkToken = null;
    this.__emailLinkFailed = false;
    this.__backToLoginOptions();
  }

  private __requestNewEmailCode() {
    this.__emailLinkToken = null;
    this.__emailLinkFailed = false;
    this.__authState = AuthState.EMAIL_AUTH;
  }

  private async __authEmail() {
    if (!this.__loader) throw new Error('Can not find loader service');

    const emailInput = this.renderRoot.querySelector('#app-login-email') as WaInput | null;
    if (!emailInput) throw new Error('Unable to find the email input field');

    const email = (emailInput.value ?? '').trim();
    if (!email) {
      this.__toast?.queueToast({ label: 'Please enter your email address', timeout: 4000 });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.__toast?.queueToast({ label: 'Please enter a valid email address', timeout: 4000 });
      return;
    }

    await this.__loader.startLoading();

    const reply = await this.__postAuthRequest(this.__authenticateEmailURL, { email });
    if (!reply) return;
    const { response, res, err } = reply;

    if (response.status !== 200 || res?.code !== 200) {
      this.__loader.finishLoading();
      this.__toast?.queueToast({ label: 'Something went wrong, please try again', timeout: 4000 });
      logger.error(`Error registering user: ${err}`);
      return;
    }

    this.__toast?.queueToast({
      label: `If there is an account for ${email}, we've emailed you a log-in link and code`,
      timeout: 4000,
    });
    this.__authenticateEmail = email;
    this.__emailChallengeToken = res.challengeToken;

    this.__authState = AuthState.EMAIL_AUTH_CHECK;

    await this.__loader.finishLoading();
  }

  private async __authEmailCheck() {
    if (!this.__loader) throw new Error('Can not find loader service');

    if (!this.__authenticateEmail || !this.__emailChallengeToken) {
      this.__toast?.queueToast({ label: 'Please enter your email address', timeout: 4000 });
      this.__authState = AuthState.EMAIL_AUTH;
      return;
    }

    const codeInput = this.renderRoot.querySelector('#app-login-email-check') as WaOtpInput;
    if (!codeInput) throw new Error('Unable to find the email input field');

    const code = codeInput.value.trim();
    if (!isValidEmailCode(code)) {
      this.__toast?.queueToast({ label: 'Please enter a valid 6-digit code', timeout: 4000 });
      return;
    }

    await this.__loader.startLoading();

    const reply = await this.__postAuthRequest(this.__authenticateEmailCheckURL, {
      challengeToken: this.__emailChallengeToken,
      code,
    });
    if (!reply) return;
    const { response, res, err } = reply;

    // A wrong, expired and used-up code are answered alike, and a new code fixes all three
    if (response.status === EMAIL_CODE_REJECTED) {
      this.__loader.finishLoading();
      this.__toast?.queueToast({ label: "That code didn't work. Check it or request a new code.", timeout: 4000 });
      return;
    }

    if (response.status !== 200 || res?.code !== 200) {
      this.__loader.finishLoading();
      this.__toast?.queueToast({ label: 'Something went wrong, please try again', timeout: 4000 });
      logger.error(`Error registering user: ${err}`);
      return;
    }

    const userToken = res.query.userToken;
    const oauthApp = res.query.oauthApp;

    window.location.href = `/?&userToken=${userToken}&oauthApp=${oauthApp}`;
  }

  private async __authEmailLink() {
    if (!this.__loader) throw new Error('Can not find loader service');
    if (!this.__emailLinkToken) return this.__requestNewEmailCode();

    await this.__loader.startLoading();

    const reply = await this.__postAuthRequest(this.__authenticateEmailLinkURL, { token: this.__emailLinkToken });
    if (!reply) {
      this.__emailLinkToken = null;
      this.__emailLinkFailed = true;
      return;
    }
    const { response, res, err } = reply;

    if (response.status !== 200 || res?.code !== 200) {
      this.__loader.finishLoading();
      this.__emailLinkToken = null;
      this.__emailLinkFailed = true;
      if (response.status !== EMAIL_CODE_REJECTED) logger.error(`Error logging in with an email link: ${err}`);
      return;
    }

    window.location.href = `/?&userToken=${res.query.userToken}&oauthApp=${res.query.oauthApp}`;
  }

  // Null when Buttress can't be reached; the loader is finished and the user told.
  private async __postAuthRequest(url: string, payload: unknown) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const { res, err } = await response.json().catch(() => ({}));
      return { response, res, err };
    } catch (err) {
      logger.error('Unable to reach the sign-in service', err);
      await this.__loader?.finishLoading();
      this.__toast?.queueToast({ label: 'Unable to reach nodeStream, please try again', timeout: 4000 });
      return null;
    }
  }

  private async __requestAnInvitation() {
    await this.__loader?.startLoading();
    this.__authState = AuthState.REQUEST_INVITATION;
    await this.__loader?.finishLoading();
  }

  private async __submitRequestAnInvitation(values: Partial<FormValues>) {
    try {
      this.__errors = {};
      this.__requestedInvitationError = undefined;

      const firstName = values.firstName;
      const lastName = values.lastName;
      const email = values.email;
      const jobTitle = values.jobTitle;
      const companyName = values.companyName;
      const registrarIdentifier = values.registrarIdentifier;

      if (!firstName) this.__errors.firstName = 'First name field is required';
      if (!lastName) this.__errors.lastName = 'Last name field is required';
      if (!email) this.__errors.email = 'Email field is required';
      if (!jobTitle) this.__errors.jobTitle = 'Job Title field is required';
      if (!companyName) this.__errors.companyName = 'Company name field is required';
      if (!registrarIdentifier) this.__errors.registrarIdentifier = 'Company registrar identifier field is required';
      if (Object.values(this.__errors).length > 0) throw new Error('Missing required fields');
      await this.__loader?.startLoading();

      const result = await callLambda(
        'demo/create-invitation',
        'POST',
        undefined,
        JSON.stringify({
          firstName,
          lastName,
          email,
          jobTitle,
          companyName,
          registrarIdentifier,
        }),
      );

      if (!result.res || result.res.code !== 200) {
        throw new Error(result.res?.message?.toString() || 'Something went wrong');
      }

      this.__requestedInvitationSent = true;
      await this.__loader?.finishLoading();
    } catch (err) {
      await this.__loader?.finishLoading();
      this.__requestedInvitationError = err instanceof Error ? err.message : 'Something went wrong';
      throw new Error(this.__requestedInvitationError);
    }
  }

  private __clearRejectedStoredToken() {
    if (this.__authState === AuthState.AUTH_REQUIRED && sessionStorage.getItem('userToken')) {
      sessionStorage.removeItem('userToken');
      sessionStorage.removeItem('oauthApp');
    }
  }

  private __logout() {
    sessionStorage.removeItem('userToken');
    sessionStorage.removeItem('oauthApp');
    // A fresh load is the only way to drop the socket, crag's store and every service's copy of the session
    window.location.replace('/');
  }

  render() {
    return html`
      <link rel="stylesheet" href="/themes/base.css" />
      <link rel="stylesheet" id="theme_styles" href="/themes/light.css" />

      ${this.__router} ${this.__settings} ${this.__auth} ${this.__dir}
      <error-directory></error-directory>

      ${this.__publicLedger} ${this.__trustScores} ${this.__stripe} ${this.__workflows} ${this.__communcation}
      ${this.__toast} ${this.__dialog} ${this.__loader}
      <!-- The in-app guide's scrim and bubble, here so they take the theme and cover onboarding and the shell -->
      ${this.__tutorial}

      <!-- TODO: Drive Structure Service -->

      <!-- TODO: Loading Dialog -->
      <!-- TODO: Error Dialog -->

      ${this.__db}

      <div id="app-container" class="${this.__themeMode}">
        ${when(
          this.__onboarding,
          () => html`
            <view-onboarding></view-onboarding>
          `,
          () => html`
            ${choose(
              this.__authState,
              [
                [
                  AuthState.LOADING,
                  () => html`
                    <div class="preload app-auth-load flex" name="loading">
                      <div class="layout vertical center fit">
                        <!-- The closed shutter covers the page while it loads -->
                        <div class="flex"></div>
                        <div class="layout vertical end-justified footer">
                          ${this.__app?.title} - ${this.__app?.version}
                        </div>
                      </div>
                    </div>
                  `,
                ],
                [
                  AuthState.ERROR,
                  () => html`
                    <div class="preload app-login app-auth-error fit" name="error">
                      <div class="layout vertical center fit">
                        <div class="layout horizontal center flex w-100">
                          <div class="layout vertical center w-100" role="alert">
                            <h1 class="app-auth-error__title">Something went wrong</h1>
                            <p class="app-auth-error__desc">nodeStream couldn't load. Please try again.</p>
                            <div class="layout vertical login-buttons">
                              ${renderProviderButton('Try again', () => window.location.reload())}
                            </div>
                          </div>
                        </div>
                        <div class="layout vertical end-justified footer">
                          ${this.__app?.title} - ${this.__app?.version}
                        </div>
                      </div>
                    </div>
                    <div class="login-image h-100" alt=""></div>
                  `,
                ],
                [
                  AuthState.AUTH_REQUIRED,
                  () => html`
                    ${this.__oauthLanding
                      ? html`
                          <view-oauth signed-out></view-oauth>
                        `
                      : this.renderAuthRequired()}
                    <div class="login-image h-100" alt=""></div>
                  `,
                ],
                [
                  AuthState.AUTHENTICATED,
                  () => html`
                    ${this.renderAuthenticated()}
                  `,
                ],
                [
                  AuthState.EMAIL_AUTH,
                  () => html`
                    ${this.renderAuthContainer(this.renderAuthEmail(), () => this.__backToLoginOptions())}
                    <div class="login-image h-100" alt=""></div>
                  `,
                ],
                [
                  AuthState.EMAIL_AUTH_CHECK,
                  () => html`
                    ${this.renderAuthContainer(this.renderAuthEmailCheck(), () => this.__backToEmailEntry())}
                    <div class="login-image h-100" alt=""></div>
                  `,
                ],
                [
                  AuthState.REQUEST_INVITATION,
                  () => html`
                    ${this.renderRequestAnInvitation()}
                    <div class="login-image h-100" alt=""></div>
                  `,
                ],
                [
                  AuthState.EMAIL_LINK,
                  () => html`
                    ${this.renderAuthContainer(this.renderAuthEmailLink(), () => this.__leaveEmailLink())}
                    <div class="login-image h-100" alt=""></div>
                  `,
                ],
              ],
              () => html`
                <div>Error</div>
              `,
            )}
          `,
        )}
      </div>
    `;
  }

  renderAuthRequired() {
    return html`
      <div class="preload app-login fit" name="authenticate">
        <div class="layout vertical center fit">
          <div class="layout horizontal center flex w-100">
            <div class="layout vertical center w-100">
              <ns-main-logo .login=${true} classes="w-100"></ns-main-logo>
              <div class="layout horizontal center-center mt-4">
                <div class="flex layout vertical login-buttons">
                  ${renderProviderButton('Log in with Google', this.__googleLogin, undefined, 'google')}
                  ${renderProviderButton('Log in with Microsoft', this.__microsoftLogin, undefined, 'microsoft')}
                  ${renderProviderButton('Log in with Email', this.__emailLogin, undefined, 'email')}
                  ${this.__isProduction
                    ? ''
                    : html`
                        <div class="layout vertical center mb-3 login-or">- OR -</div>
                        ${renderProviderButton(
                          'Request an invitation',
                          this.__requestAnInvitation,
                          'white',
                          'invitation',
                        )}
                      `}
                </div>
              </div>
            </div>
          </div>
          <div class="layout vertical end-justified footer">${this.__app?.title} - v${this.__app?.version}</div>
        </div>
      </div>
    `;
  }

  renderRequestAnInvitation() {
    const { form, register } = this.__createFormController;
    const formState = form.getState();

    return html`
      <div class="layout vertical center-center fit request-invitation-page h-100">
        ${this.renderAuthBackButton(() => this.__backToLoginOptions())}
        ${when(
          this.__requestedInvitationSent,
          () => html`
            <div class="layout vertical center-center request-an-invitation green">
              <img alt="Success" src="/images/icons/success.svg" />
              <div>Email was sent Successfully!</div>
              <div>Please follow the instructions provided in the email.</div>
            </div>
          `,
          () => html`
            <div class="layout vertical center-center request-an-invitation">
              <div class="large mb-2">Request An Invitation</div>
              <div class="small mb-2">
                nodeStream needs your contact information to send the invitation to the correct email address.
              </div>
              ${this.__requestedInvitationError
                ? html`
                    <div role="alert" class="error-text medium mb-2">${this.__requestedInvitationError}</div>
                  `
                : ''}
              <form
                class="w-100"
                @submit=${(e: CustomEvent) => {
                  e.preventDefault();
                  form.submit();
                }}
              >
                <div class="layout vertical mb-2">
                  <ns-input-field
                    required
                    ${register('firstName')}
                    placeholder="First Name"
                    label="First Name*:"
                  ></ns-input-field>
                  ${renderFormErrorSpan('firstName', this.__errors)}
                </div>

                <div class="layout vertical mb-2">
                  <ns-input-field
                    required
                    ${register('lastName')}
                    placeholder="Last Name"
                    label="Last Name*:"
                  ></ns-input-field>
                  ${renderFormErrorSpan('lastName', this.__errors)}
                </div>

                <div class="layout vertical mb-2">
                  <ns-input-field required ${register('email')} placeholder="Email" label="Email*:"></ns-input-field>
                  ${renderFormErrorSpan('email', this.__errors)}
                </div>

                <div class="layout vertical mb-2">
                  <ns-input-field
                    required
                    ${register('jobTitle')}
                    placeholder="Job Title"
                    label="Job Title*:"
                  ></ns-input-field>
                  ${renderFormErrorSpan('jobTitle', this.__errors)}
                </div>

                <div class="layout vertical mb-2">
                  <ns-input-field
                    required
                    ${register('companyName')}
                    placeholder="Company Name"
                    label="Company Name*:"
                  ></ns-input-field>
                  ${renderFormErrorSpan('companyName', this.__errors)}
                </div>

                <div class="layout vertical mb-2">
                  <ns-input-field
                    required
                    ${register('registrarIdentifier')}
                    placeholder="Company Number"
                    label="Company Number*:"
                  ></ns-input-field>
                  ${renderFormErrorSpan('registrarIdentifier', this.__errors)}
                </div>

                <div class="layout horizontal">
                  <div class="flex"></div>
                  <wa-button variant="brand" type="submit" ?disabled=${formState.submitting}>Submit</wa-button>
                </div>
              </form>
            </div>
          `,
        )}
      </div>
    `;
  }

  renderAuthenticated() {
    return html`
      ${when(
        this.__dbLoading,
        // The closed shutter covers the page while the database connects
        () => nothing,
        () => html`
          <div id="main-container">
            <div class="layout horizontal h-100">
              <ns-side-bar
                id="mainMenu"
                fixed
                logo="/images/logo-icon.png"
                .topMenu="${this.__getSideBarTopMenu()}"
                .bottomMenu="${this.__getSideBarBottomMenu()}"
                .activeItems="${this.__sideBarActiveItems}"
                ?inert="${this.__anyOverlayOpen}"
              ></ns-side-bar>
              <div id="app-content">
                <div class="layout vertical h-100">
                  <!-- TODO: Move the top-bar into the views. -->
                  <ns-top-bar
                    .heading=${this.__views[this.__view]?.title}
                    ?greeting=${this.__view === 'feed'}
                    ?inert="${this.__anyOverlayOpen}"
                  ></ns-top-bar>
                  <ns-start-menu ?open="${this.__isStartMenuOpen}" ?inert="${!this.__isStartMenuOpen}"></ns-start-menu>
                  <ns-background-tasks
                    ?open="${this.__isTasksMenuOpen}"
                    ?inert="${!this.__isTasksMenuOpen}"
                  ></ns-background-tasks>
                  <ns-dev-tools-panel
                    ?open="${this.__isDevToolsOpen}"
                    ?inert="${!this.__isDevToolsOpen}"
                  ></ns-dev-tools-panel>
                  <ns-notification-dropdown
                    ?open="${this.__isNotificationsOpen}"
                    ?inert="${!this.__isNotificationsOpen}"
                  ></ns-notification-dropdown>
                  <div
                    class="menu-backdrop"
                    ?open="${this.__anyOverlayOpen}"
                    @click="${() => this.__closeStartMenu()}"
                    @keydown="${(e: KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.__closeStartMenu();
                      }
                    }}"
                    tabindex="0"
                    role="button"
                    aria-label="Close menu"
                  ></div>
                  <div id="view-container" role="main" ?inert="${this.__anyOverlayOpen}">
                    ${choose(
                      this.__view,
                      [
                        [
                          'company',
                          () => html`
                            <view-company></view-company>
                          `,
                        ],
                        [
                          'feed',
                          () => html`
                            <view-feed></view-feed>
                          `,
                        ],
                        [
                          'workflows',
                          () => html`
                            <view-workflows></view-workflows>
                          `,
                        ],
                        [
                          'connections',
                          () => html`
                            <view-connections></view-connections>
                          `,
                        ],
                        [
                          'org-chart',
                          () => html`
                            <view-org-chart></view-org-chart>
                          `,
                        ],
                        [
                          'notifications',
                          () => html`
                            <view-notifications></view-notifications>
                          `,
                        ],
                        [
                          'companies',
                          () => html`
                            <view-companies></view-companies>
                          `,
                        ],
                        [
                          'relationships',
                          () => html`
                            <view-relationships></view-relationships>
                          `,
                        ],
                        [
                          'data',
                          () => html`
                            <view-data></view-data>
                          `,
                        ],
                        [
                          'workflow-studio',
                          () => html`
                            <view-workflow-studio></view-workflow-studio>
                          `,
                        ],
                        [
                          'my-account',
                          () => html`
                            <view-account></view-account>
                          `,
                        ],
                        [
                          'oauth',
                          () => html`
                            <view-oauth></view-oauth>
                          `,
                        ],
                      ],
                      () => html`
                        <div class="layout vertical center-center h-100 page-not-found">
                          <ns-icon icon="question-mark" class="mb-3"></ns-icon>
                          <div class="page-not-found__code mb-1">404</div>
                          <div class="page-not-found__title mb-2">Page not found</div>
                          <div class="page-not-found__subtitle mb-4">
                            The page you're looking for doesn't exist or may have moved.
                          </div>
                          <wa-button variant="brand" @click=${() => viewPath('/feed')}>Back to Feed</wa-button>
                        </div>
                      `,
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        `,
      )}
    `;
  }

  renderAuthEmail() {
    return html`
      <div id="card" class="card card--expanded stacked">
        <div class="header">
          <div class="title">Log in with Email</div>
          <div class="under-title">Enter you're email below and we'll send you an access code to login with.</div>
        </div>
        <div class="body">
          <form
            id="app-login-email-form"
            class="layout vertical mt-2 mb-2"
            novalidate
            @submit=${(e: Event) => {
              e.preventDefault();
              this.__authEmail();
            }}
          >
            <wa-input
              class="secondary"
              id="app-login-email"
              name="email"
              type="email"
              required
              placeholder="Enter your email address"
              label="Enter your email address"
            ></wa-input>
          </form>
        </div>
        <div class="buttons">
          <div class="layout">
            <wa-button class="raised" variant="brand" type="submit" form="app-login-email-form">
              Send Access Code
            </wa-button>
          </div>
        </div>
      </div>
    `;
  }

  renderAuthEmailCheck() {
    return html`
      <div class="auth-email-card">
        <div class="layout vertical">
          ${renderEmailCodeCard({
            inputId: 'app-login-email-check',
            subtitle: "We've emailed you a log-in link and a code. Use the link, or enter the code below:",
            onVerify: () => this.__authEmailCheck(),
          })}
        </div>
      </div>
    `;
  }

  renderAuthEmailLink() {
    return html`
      <div class="auth-email-card">
        <div class="layout vertical">
          ${renderEmailLinkCard({
            failed: this.__emailLinkFailed,
            onLogIn: () => this.__authEmailLink(),
            onRequestNew: () => this.__requestNewEmailCode(),
          })}
        </div>
      </div>
    `;
  }

  renderAuthBackButton(onBack: () => void) {
    return html`
      <wa-button appearance="plain" class="icon-button circle black-bg auth-back" @click=${onBack}>
        <ns-icon icon="chevron-left" aria-hidden="true"></ns-icon>
        <span class="visually-hidden">Back</span>
      </wa-button>
    `;
  }

  renderAuthContainer(content: TemplateResult, onBack: () => void) {
    return html`
      <div class="preload app-login fit">
        ${this.renderAuthBackButton(onBack)}
        <div class="layout vertical center fit">
          <div class="layout horizontal center flex">
            <div class="layout vertical center-center">${content}</div>
          </div>
          <div class="layout vertical end-justified footer">${this.__app?.title} - v${this.__app?.version}</div>
        </div>
      </div>
    `;
  }
}
